import SinglePropTracker from "../../../prop-trackers/single-prop-tracker.js";

import { MaterialTypes_T } from "../../data-defs/material-types.js";
import { getMaterialFullTypeKey as getKey } from "../../data-defs/material-full-types.js";

import MaterialTransparencyTrackerAssigner from "./materials/material-transparency-tracker-assigner.js";
import PbrMaterialPropertyTrackerAssigner from "./materials/pbr-material-property-tracker-assigner.js";
import PbrMaterialTextureTrackerAssigner from "./materials/pbr-material-texture-tracker-assigner.js";
import PbrMaterialEnvLightTrackerAssigner from "./materials/pbr-material-envlight-tracker-assigner.js";

const PbrBasicGenProps = [
    "aoMapIntensity", "depthTest", "depthWrite", "opacity", "premultipliedAlpha",
    "side", "wireframe", "flatShading",
    "morphTargets", "morphNormals", "skinning",
    "color",
];
const PbrBasicTexProps = ["map", "alphaMap", "aoMap"];

const PbrRegularGenProps = [
    "aoMapIntensity", "bumpScale", "depthTest", "depthWrite", "emissiveIntensity", "envMapIntensity", "metalness", "roughness",
    "side", "wireframe", "flatShading",
    "morphTargets", "morphNormals", "skinning",
    "color", "emissive",
    "normalScale",
];
const PbrRegularTexProps = ["alphaMap", "aoMap", "bumpMap", "emissiveMap", "map", "metalnessMap", "normalMap", "roughnessMap"];

const pbrBasicKey = getKey({ type: MaterialTypes_T.PBR, materialType: "basic" });
const pbrRegularKey = getKey({ type: MaterialTypes_T.PBR, materialType: "pbr" });

const MatTypes = [pbrBasicKey, pbrRegularKey];

class MaterialProperties {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, matPropControllers) {
        this._evtCtx = evtCtx;

        this._matGenDataPtrTrackers = {};
        this._matTexDataPtrTrackers = {};
        for (const type of MatTypes) {
            this._matGenDataPtrTrackers[type] = new SinglePropTracker();
            this._matTexDataPtrTrackers[type] = new SinglePropTracker();
        }

        const pbrBasicPropCtrl = matPropControllers[pbrBasicKey];
        this._pbrBasicGenTrackAssigner = new PbrMaterialPropertyTrackerAssigner(evtCtx, PbrBasicGenProps, pbrBasicPropCtrl.generic);
        this._pbrBasicTransTrackAssigner = new MaterialTransparencyTrackerAssigner(evtCtx, pbrBasicPropCtrl.generic);
        this._pbrBasicTexTrackAssigner = new PbrMaterialTextureTrackerAssigner(evtCtx, shlWrapCtx, shlEvtCtx, PbrBasicTexProps, pbrBasicPropCtrl.texture);

        const pbrRegularPropCtrl = matPropControllers[pbrRegularKey];
        this._pbrRegularGenTrackAssigner = new PbrMaterialPropertyTrackerAssigner(evtCtx, PbrRegularGenProps, pbrRegularPropCtrl.generic);
        this._pbrRegularTransTrackerAssigner = new MaterialTransparencyTrackerAssigner(evtCtx, pbrRegularPropCtrl.generic);
        this._pbrRegularTexTrackAssigner = new PbrMaterialTextureTrackerAssigner(evtCtx, shlWrapCtx, shlEvtCtx, PbrRegularTexProps, pbrRegularPropCtrl.texture);
        this._pbrRegularEnvLightTrackerAssigner = new PbrMaterialEnvLightTrackerAssigner(evtCtx, shlWrapCtx, shlEvtCtx, pbrRegularPropCtrl.envLight);
    }

    trackMaterialData(matGenDataByType, matTexDataByType) {
        for (const type of MatTypes) {
            const genTracker = this._matGenDataPtrTrackers[type];
            const texTracker = this._matTexDataPtrTrackers[type];

            if (matGenDataByType) {
                const { data, events } = matGenDataByType[type];
                genTracker.track(data, events, "value");
            } else {
                genTracker.track();
            }

            if (matTexDataByType) {
                const { data, events } = matTexDataByType[type];
                texTracker.track(data, events, "value");
            } else {
                texTracker.track();
            }
        }
    }

    trackMaterials(typedMat) {
        if (typedMat) {
            const pbrBasicMat = typedMat[pbrBasicKey];
            this._pbrBasicGenTrackAssigner.trackMaterials(pbrBasicMat.data, pbrBasicMat.events);
            this._pbrBasicTransTrackAssigner.trackMaterials(pbrBasicMat.data, pbrBasicMat.events);
            this._pbrBasicTexTrackAssigner.trackMaterials(pbrBasicMat.data, pbrBasicMat.events);

            const pbrRegularMat = typedMat[pbrRegularKey];
            this._pbrRegularGenTrackAssigner.trackMaterials(pbrRegularMat.data, pbrRegularMat.events);
            this._pbrRegularTransTrackerAssigner.trackMaterials(pbrRegularMat.data, pbrRegularMat.events);
            this._pbrRegularTexTrackAssigner.trackMaterials(pbrRegularMat.data, pbrRegularMat.events);
            this._pbrRegularEnvLightTrackerAssigner.trackMaterials(pbrRegularMat.data, pbrRegularMat.events);
        } else {
            this._pbrBasicGenTrackAssigner.trackMaterials();
            this._pbrBasicTransTrackAssigner.trackMaterials();
            this._pbrBasicTexTrackAssigner.trackMaterials();

            this._pbrRegularGenTrackAssigner.trackMaterials();
            this._pbrRegularTransTrackerAssigner.trackMaterials();
            this._pbrRegularTexTrackAssigner.trackMaterials();
            this._pbrRegularEnvLightTrackerAssigner.trackMaterials();
        }
    }

    trackTextures(textures) {
        if (textures) {
            this._pbrBasicTexTrackAssigner.trackTextures(textures.data, textures.events);
            this._pbrRegularTexTrackAssigner.trackTextures(textures.data, textures.events);
        } else {
            this._pbrBasicTexTrackAssigner.trackTextures();
            this._pbrRegularTexTrackAssigner.trackTextures();
        }
    }

    trackEnvLight(envLight) {
        if (envLight) {
            this._pbrRegularEnvLightTrackerAssigner.trackEnvLight(envLight.data, envLight.events);
        } else {
            this._pbrRegularEnvLightTrackerAssigner.trackEnvLight();
        }
    }

    update() {
        this._matGenDataPtrTrackers[pbrBasicKey].updateIfNeeded(({ prop: { newValue: data } }) => {
            const events = data ? this._evtCtx.getEvents(data) : null;

            this._pbrBasicGenTrackAssigner.trackMaterialData(data, events);
            this._pbrBasicTransTrackAssigner.trackMaterialData(data, events);
        });

        this._matTexDataPtrTrackers[pbrBasicKey].updateIfNeeded(({ prop: { newValue: data } }) => {
            const events = data ? this._evtCtx.getEvents(data) : null;

            this._pbrBasicTexTrackAssigner.trackMaterialData(data, events);
        });

        this._matGenDataPtrTrackers[pbrRegularKey].updateIfNeeded(({ prop: { newValue: data } }) => {
            const events = data ? this._evtCtx.getEvents(data) : null;

            this._pbrRegularGenTrackAssigner.trackMaterialData(data, events);
            this._pbrRegularTransTrackerAssigner.trackMaterialData(data, events);
        });

        this._matTexDataPtrTrackers[pbrRegularKey].updateIfNeeded(({ prop: { newValue: data } }) => {
            const events = data ? this._evtCtx.getEvents(data) : null;

            this._pbrRegularTexTrackAssigner.trackMaterialData(data, events);
        });

        this._pbrBasicGenTrackAssigner.update();
        this._pbrBasicTransTrackAssigner.update();
        this._pbrBasicTexTrackAssigner.update();

        this._pbrRegularGenTrackAssigner.update();
        this._pbrRegularTransTrackerAssigner.update();
        this._pbrRegularTexTrackAssigner.update();
        this._pbrRegularEnvLightTrackerAssigner.update();
    }
}

export default MaterialProperties;
